import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {swal, swalConfirm, swalError, swalLoading, swalSuccess} from "../../../helpers/SweetAlertWrapper";
import Page from "../../../components/BaseComponents/Page/Page";
import {FaRegularIcon, FaSolidIcon} from "../../../components/FontAwesomeIcons";
import AppConfig from "../../../config/AppConfig";
import standardEventCover from "../../../img/coriandoli.png";
import {
    getUserEventThemeColorHex,
    USER_EVENT_THEME_FONTS,
    USER_PHOTO_ALBUM_DOWNLOAD_DEADLINE_DAYS
} from "../CreateEvent/CreateEventConstants";
import {UsersModulePhotoAlbumsAPI} from "../../../api/UsersModulePhotoAlbums/UsersModulePhotoAlbumsAPI";
import ShortLinkAPI from "../../../api/ShortLink/ShortLinkAPI";
import {BookizonAppManager} from "../../../index";

export class UserPhotoAlbumPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            albumInfo: {},
            fetching: true,
            deleting: false,
            requestingPhotoDownload: false
        };
        this.coverStyle = {
            minHeight: "260px",
            backgroundSize: "cover",
            backgroundPosition: "center",
            overflow: "hidden",
        };
        this.coverOverlayBaseStyle = {
            minHeight: "260px",
        };
        this.iconBoxStyle = {
            width: "38px",
            minWidth: "38px",
            height: "38px",
        };
        this.colorDotStyle = {
            width: "12px",
            minWidth: "12px",
            height: "12px",
        };
        this.qrImageStyle = {
            width: "210px",
            maxWidth: "100%",
        };
        this.emptyBoxStyle = {
            minHeight: "220px",
        }
    }

    componentDidMount() {
        this.fetchAlbum()
    }

    fetchAlbum = _ => {
        this.setState({fetching: true})
        UsersModulePhotoAlbumsAPI.Admin.Album.getFullAlbumInfo(this.props.photoAlbumId)
            .then(albumInfo => {
                this.setState({albumInfo: albumInfo || {}, fetching: false})
            })
            .catch(error => {
                swalError(error)
                this.setState({fetching: false})
            })
    }

    formatDate = date => {
        const parsedDate = date ? moment(date) : null;
        return parsedDate && parsedDate.isValid() ? parsedDate.format("DD MMMM YYYY HH:mm") : null;
    }

    getAlbumEndDate = albumInfo => {
        const endDate = albumInfo.end_date ? moment(albumInfo.end_date) : null;
        return endDate && endDate.isValid() ? endDate : null;
    }

    isAlbumEnded = albumInfo => {
        const endDate = this.getAlbumEndDate(albumInfo);
        return endDate ? moment().isAfter(endDate) : false;
    }

    getPhotoDownloadDeadline = albumInfo => {
        const endDate = this.getAlbumEndDate(albumInfo);
        return endDate ? moment(endDate).add(USER_PHOTO_ALBUM_DOWNLOAD_DEADLINE_DAYS, "days") : null;
    }

    getPhotoDownloadCountdown = albumInfo => {
        const deadline = this.getPhotoDownloadDeadline(albumInfo);
        if (!deadline) {
            return null;
        }

        const now = moment();
        if (now.isAfter(deadline)) {
            return "Tempo scaduto";
        }

        const daysLeft = deadline.diff(now, "days");
        if (daysLeft > 0) {
            return `${daysLeft} ${daysLeft === 1 ? "giorno rimanente" : "giorni rimanenti"}`;
        }

        const hoursLeft = deadline.diff(now, "hours");
        if (hoursLeft > 0) {
            return `${hoursLeft} ${hoursLeft === 1 ? "ora rimanente" : "ore rimanenti"}`;
        }

        return "Scade oggi";
    }

    getPhotoDownloadRequestDate = albumInfo => {
        const requestDate = albumInfo.download_request_at ? moment(albumInfo.download_request_at) : null;
        return requestDate && requestDate.isValid() ? requestDate : null;
    }

    formatDateRange = albumInfo => {
        const startDate = this.formatDate(albumInfo.start_date);
        const endDate = this.formatDate(albumInfo.end_date);
        if (startDate && endDate && startDate !== endDate) {
            return `${startDate} - ${endDate}`;
        }
        return startDate || endDate || "Data non impostata";
    }

    getAlbumStyleConfig = albumInfo => {
        const settings = albumInfo.settings || {};
        const selectedThemeColor = getUserEventThemeColorHex(settings.theme_color);
        const selectedFont = USER_EVENT_THEME_FONTS[settings.theme_font] || USER_EVENT_THEME_FONTS.roboto;
        const imageUrl = !albumInfo.image_url ?
            standardEventCover :
            albumInfo.image_url.indexOf("http") === 0 ?
                albumInfo.image_url :
                `${AppConfig.webServerUrl}${albumInfo.image_url.indexOf("/") === 0 ? albumInfo.image_url : `/${albumInfo.image_url}`}`;

        return {
            fontImport: selectedFont.import,
            selectedColor: selectedThemeColor,
            coverStyle: {
                ...this.coverStyle,
                backgroundImage: `url(${imageUrl})`,
                fontFamily: selectedFont.property.replace(" !important", ""),
            },
            coverOverlayStyle: {
                ...this.coverOverlayBaseStyle,
                background: `linear-gradient(180deg, ${selectedThemeColor}33 0%, ${selectedThemeColor}dd 100%)`,
            }
        };
    }

    renderLoading = _ => (
        <div className={"d-flex align-items-center justify-content-center text-muted"} style={this.emptyBoxStyle}>
            <i className="fa fa-spin fa-spinner"/>
        </div>
    )

    renderPhotoDownloadCountdown = albumInfo => {
        if (!this.isAlbumEnded(albumInfo)) {
            return null;
        }

        const deadline = this.getPhotoDownloadDeadline(albumInfo);
        if (!deadline) {
            return null;
        }

        const requestDate = this.getPhotoDownloadRequestDate(albumInfo);

        return (
            <div className={"card border-0 shadow-sm mb-3"}>
                <div className={"card-body"}>
                    <div className={`d-flex align-items-center${requestDate ? "" : " mb-3"}`}>
                        <div className={"rounded bg-light d-flex align-items-center justify-content-center text-warning mr-3"} style={this.iconBoxStyle}>
                            <FaSolidIcon name={"download"}/>
                        </div>
                        <div>
                            {
                                requestDate ?
                                    <React.Fragment>
                                        <div className={"small text-muted"}>Richiesta download inviata</div>
                                        <div className={"font-weight-bold"}>{requestDate.format("DD MMMM YYYY HH:mm")}</div>
                                    </React.Fragment> :
                                    <React.Fragment>
                                        <div className={"small text-muted"}>Tempo per scaricare le foto</div>
                                        <div className={"font-weight-bold"}>{this.getPhotoDownloadCountdown(albumInfo)}</div>
                                        <div className={"small text-muted"}>
                                            Scadenza: {deadline.format("DD MMMM YYYY HH:mm")}
                                        </div>
                                    </React.Fragment>
                            }
                        </div>
                    </div>
                    {
                        !requestDate &&
                        <button
                            type={"button"}
                            className={"btn btn-primary btn-block"}
                            onClick={this.handleRequestPhotoDownload}
                            disabled={this.state.requestingPhotoDownload}
                        >
                            {
                                this.state.requestingPhotoDownload ?
                                    <React.Fragment>
                                        <i className="fa fa-spin fa-spinner"/> Invio richiesta...
                                    </React.Fragment> :
                                    <React.Fragment>
                                        Richiedi download foto <FaSolidIcon name={"download"}/>
                                    </React.Fragment>
                            }
                        </button>
                    }
                </div>
            </div>
        );
    }

    handleShareAlbumLink = _ => {
        const albumPageLink = this.state.albumInfo.album_link || null;
        swalLoading("Attendi, stiamo generando il link")
        ShortLinkAPI.generate(albumPageLink)
            .then(data => {
                const message = `Ciao, guarda e carica le foto del mio album ${this.state.albumInfo.name || ""}: ${data.short_link}`;
                BookizonAppManager.shareData(message);
                swal.safeClose();
            })
            .catch(error => {
                swal.safeClose();
                swalError(error);
            })
    }

    handleRequestPhotoDownload = _ => {
        this.setState({requestingPhotoDownload: true});
        swalLoading("Invio richiesta download foto...");
        UsersModulePhotoAlbumsAPI.Admin.Album.requestPhotoDownload(this.props.photoAlbumId)
            .then(response => {
                const message = response && response.message ? response.message : response;
                swal.safeClose();
                swalSuccess(message || "Richiesta download inviata correttamente.").then(this.fetchAlbum);
                this.setState({requestingPhotoDownload: false});
            })
            .catch(error => {
                swal.safeClose();
                swalError(error);
                this.setState({requestingPhotoDownload: false});
            });
    }

    handleDeleteAlbum = _ => {
        swalConfirm(
            "Sei sicuro di voler eliminare questo album fotografico? L'azione non potrà essere annullata.",
            "Elimina",
            "Annulla"
        )
            .then(_ => {
                this.setState({deleting: true});
                swalLoading("Eliminazione album in corso...");
                UsersModulePhotoAlbumsAPI.Admin.Album.delete(this.props.photoAlbumId)
                    .then(response => {
                        swal.safeClose();
                        swalSuccess(response.message || "Album eliminato correttamente.").then(_ => {
                            this.setState({deleting: false});
                            this.props.onDismiss();
                        });
                    })
                    .catch(error => {
                        swal.safeClose();
                        swalError(error);
                        this.setState({deleting: false});
                    });
            })
            .catch(_ => null);
    }

    render() {
        const albumInfo = this.state.albumInfo || {};
        const settings = albumInfo.settings || {};
        const styleConfig = this.getAlbumStyleConfig(albumInfo);
        const albumName = albumInfo.name || "Album fotografico";
        const dateRange = this.formatDateRange(albumInfo);
        const location = albumInfo.location_name || albumInfo.location_address || "Luogo non impostato";
        const description = albumInfo.description || "Nessuna descrizione inserita.";
        const photosCount = parseInt(albumInfo.photos_count, 10) || 0;

        return (
            <Page
                title={albumName}
                leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                onLeftIconClick={this.props.onDismiss}
                active={true}
            >
                {
                    this.state.fetching ?
                        this.renderLoading() :
                        <div className={"container py-3"}>
                            <style>
                                {styleConfig.fontImport}
                            </style>

                            <div className={"card border-0 shadow-sm overflow-hidden mb-3"}>
                                <div style={styleConfig.coverStyle}>
                                    <div className={"d-flex flex-column justify-content-end text-white p-3"} style={styleConfig.coverOverlayStyle}>
                                        <h4 className={"font-weight-bold mb-2"}>{albumName}</h4>
                                        <div className={"small"}>
                                            <FaRegularIcon name={"calendar"} className={"mr-1"}/>
                                            {dateRange}
                                            {
                                                albumInfo.location_name &&
                                                <span className={"ml-2"}>
                                                    <FaSolidIcon name={"map-marker-alt"} className={"mr-1"}/>
                                                    {albumInfo.location_name}
                                                </span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {
                                this.state.albumInfo.album_link &&
                                <div>
                                    <div className={"d-flex mb-3 align-items-center justify-content-around"}>
                                        <button
                                            type={"button"}
                                            className={"btn btn-primary p-2 w-50 ml-1"}
                                            onClick={this.handleShareAlbumLink}
                                        >
                                            Condividi <FaSolidIcon name={"share"}/>
                                        </button>
                                        <a
                                            className={"btn btn-info p-2 w-50 ml-1"}
                                            href={this.state.albumInfo.album_link}
                                        >
                                            Vedi pagina <FaSolidIcon name={"eye"}/>
                                        </a>
                                    </div>

                                    <div className={"card border-0 shadow-sm mb-3"}>
                                        <div className={"card-body text-center"}>
                                            <h6 className={"font-weight-bold mb-3"}>QR album</h6>
                                            <img
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=420x420&data=${encodeURIComponent(this.state.albumInfo.album_link)}`}
                                                className={"img-fluid"}
                                                style={this.qrImageStyle}
                                                alt={"QR album fotografico"}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }


                            <div className={"card border-0 shadow-sm mb-3"}>
                                <div className={"card-body"}>
                                    <h6 className={"font-weight-bold mb-2"}>Dettagli album</h6>
                                    <div className={"text-muted"}>{description}</div>
                                </div>
                            </div>

                            {this.renderPhotoDownloadCountdown(albumInfo)}

                            <div className={"card border-0 shadow-sm mb-3"}>
                                <div className={"card-body"}>
                                    <div className={"d-flex align-items-center mb-3"}>
                                        <div className={"rounded bg-light d-flex align-items-center justify-content-center text-primary mr-3"} style={this.iconBoxStyle}>
                                            <FaSolidIcon name={"images"}/>
                                        </div>
                                        <div>
                                            <div className={"small text-muted"}>Foto caricate</div>
                                            <div className={"font-weight-bold"}>{photosCount}</div>
                                        </div>
                                    </div>
                                    <div className={"d-flex align-items-center mb-3"}>
                                        <div className={"rounded bg-light d-flex align-items-center justify-content-center text-primary mr-3"} style={this.iconBoxStyle}>
                                            <FaSolidIcon name={"map-marker-alt"}/>
                                        </div>
                                        <div>
                                            <div className={"small text-muted"}>Luogo</div>
                                            <div className={"font-weight-bold"}>{location}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={"text-center my-4"}>
                                <button
                                    type={"button"}
                                    className={"btn btn-sm btn-link text-danger"}
                                    onClick={this.handleDeleteAlbum}
                                    disabled={this.state.deleting}
                                >
                                    <FaSolidIcon name={"trash"}/> Elimina album
                                </button>
                            </div>
                        </div>
                }
            </Page>
        );
    }
}

UserPhotoAlbumPage.propTypes = {
    onDismiss: PropTypes.func,
    photoAlbumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
